import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, LogIn, Eye, EyeOff, Fingerprint } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { isPasskeySupported, startPasskeyLogin, startPasskeyRegistration } from '../lib/passkey';

const InicioSesionPage = () => {
  const navigate = useNavigate();
  const { setTokenAndUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [showSavePasskey, setShowSavePasskey] = useState(false);
  const [savingPasskey, setSavingPasskey] = useState(false);
  const passkeyOk = isPasskeySupported();

  const googleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPasskey = async () => {
    setPasskeyLoading(true);
    try {
      const result = await startPasskeyLogin(api);
      setTokenAndUser(result.token, result.user);
      toast({ title: '¡Bienvenido!', description: result.user.name || result.user.email });
      navigate('/');
    } catch (err) {
      if (err?.name !== 'NotAllowedError') {
        toast({ title: 'No se pudo usar la llave', description: err?.message || 'Intenta con contraseña.' });
      }
    } finally { setPasskeyLoading(false); }
  };

  const savePasskey = async () => {
    setSavingPasskey(true);
    try {
      await startPasskeyRegistration(api, navigator.userAgent.includes('iPhone') ? 'iPhone' : navigator.userAgent.includes('Android') ? 'Android' : 'Mi dispositivo');
      toast({ title: 'Llave guardada', description: 'La próxima vez puedes entrar con tu huella.' });
      setShowSavePasskey(false);
    } catch (err) {
      if (err?.name !== 'NotAllowedError') {
        toast({ title: 'No se pudo guardar', description: err?.message || 'Intenta de nuevo.' });
      }
    } finally { setSavingPasskey(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post('/auth/login', { email: email.trim().toLowerCase(), password });
      setTokenAndUser(r.data.token, r.data.user);
      toast({ title: '¡Bienvenido de vuelta!', description: r.data.user.name || r.data.user.email });
      if (passkeyOk) setShowSavePasskey(true);
      else navigate('/');
    } catch (err) {
      toast({ title: 'No pudimos ingresarte', description: err?.response?.data?.detail || 'Intenta de nuevo.' });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-orange-500/15 blur-[100px]" />
        <div className="absolute -bottom-20 -right-20 w-[380px] h-[380px] rounded-full bg-red-600/10 blur-[100px]" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <BrandLogo size="md" />
        </div>

        <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-8 backdrop-blur">
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Inicia <span className="text-orange-400">sesión</span>
          </h1>
          <p className="mt-2 text-white/60 text-sm">Accede a tu cuenta para ver pedidos y gestionar tus datos.</p>

          {/* Google */}
          <button
            type="button"
            onClick={googleLogin}
            className="mt-6 w-full h-12 rounded-full bg-white text-neutral-900 font-bold text-sm flex items-center justify-center gap-3 hover:bg-white/90 transition-colors shadow-md shadow-white/5"
            data-testid="login-google-btn"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden>
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.9 1.5l2.6-2.5C16.9 3.4 14.7 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c6.9 0 11.4-4.8 11.4-11.6 0-.8-.1-1.4-.2-2H12z"/>
            </svg>
            Continuar con Google
          </button>

          {/* Passkey */}
          {passkeyOk && (
            <button
              type="button"
              onClick={loginWithPasskey}
              disabled={passkeyLoading}
              data-testid="login-passkey-btn"
              className="mt-3 w-full h-12 rounded-full border border-orange-500/40 text-orange-400 font-bold text-sm flex items-center justify-center gap-2 hover:bg-orange-500/10 transition-colors disabled:opacity-50"
            >
              {passkeyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
              Ingresar con huella / llave de acceso
            </button>
          )}

          <div className="mt-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-white/30">
            <div className="flex-1 h-px bg-white/10" /> o con correo <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4" autoComplete="on" data-testid="login-form">            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Correo electrónico</label>
              <div className="relative mt-2">
                <Mail className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Contraseña</label>
                <Link to="/recuperar-contrasena" className="text-[11px] font-bold text-orange-400 hover:text-orange-300" data-testid="login-forgot-link">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <div className="relative mt-2">
                <Lock className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input
                  type={showPw ? 'text' : 'password'}
                  name="current-password"
                  autoComplete="current-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  className="h-12 pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80" aria-label="Mostrar contraseña">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-13 h-14 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-base shadow-lg shadow-orange-500/25" data-testid="login-submit-btn">
              {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Ingresando…</> : <><LogIn className="w-4 h-4 mr-2" /> Ingresar</>}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-white/60">
            ¿Nuevo aquí? <Link to="/registro" className="text-orange-400 font-bold hover:text-orange-300">Crea tu cuenta</Link>
          </p>
        </div>
      </div>

      {/* Save passkey prompt after password login */}
      {showSavePasskey && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl border border-orange-500/30 bg-[#111] p-6 shadow-2xl shadow-orange-500/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Fingerprint className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">¿Iniciar sesión más rápido?</p>
                <p className="text-white/50 text-xs">Guarda una llave de acceso con tu huella o Face ID</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button
                onClick={savePasskey}
                disabled={savingPasskey}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full"
                data-testid="save-passkey-btn"
              >
                {savingPasskey ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar llave'}
              </Button>
              <Button
                onClick={() => { setShowSavePasskey(false); navigate('/'); }}
                variant="ghost"
                className="flex-1 text-white/60 hover:text-white rounded-full"
                data-testid="skip-passkey-btn"
              >
                Ahora no
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InicioSesionPage;
